/**
 * React Query hook for current authenticated user
 * Provides hook for fetching the current user's role and permissions
 */
import { useQuery } from '@tanstack/react-query'
import { fetchCurrentUser, adminKeys } from '@/api/admin'
import { useAuth } from '@/hooks/useAuth'
import type { CurrentUser } from '@/types/admin'

interface UseCurrentUserOptions {
  /** Whether to enable the query. Defaults to true. */
  enabled?: boolean
}

/**
 * Hook for fetching current authenticated user info
 * Fetches from GET /api/me endpoint
 * 
 * Waits for the auth client to be fully initialized before making requests
 * to avoid 401 errors during the auth initialization phase.
 */
export function useCurrentUser(options: UseCurrentUserOptions = {}) {
  const { enabled = true } = options
  const { isInitialized, isAuthenticated, isLoading: isAuthLoading } = useAuth()
  
  // Only enable the query when:
  // 1. the auth client is fully initialized
  // 2. User is authenticated  
  // 3. Auth is not loading (token acquisition complete)
  // 4. Caller hasn't disabled the query
  const isAuthReady = isInitialized && isAuthenticated && !isAuthLoading
  
  return useQuery<CurrentUser>({
    queryKey: adminKeys.currentUser(),
    queryFn: fetchCurrentUser,
    staleTime: 1000 * 60 * 5, // 5 minutes - role doesn't change often
    enabled: enabled && isAuthReady,
  })
}
